import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { CurrentUserSelectors, UserSelectors } from '@core/account-data';
import { SelectAdminsAnalytics } from '@core/account-logic';
import { AdditionalUserSelectionOption, CurrentUser, User } from '@core/account-types';
import { AdminsSelectDialogFormComponent } from '@core/account-ui';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import { DialogRef } from '@core/user-interface-legacy';

export type AdminsSelectDialogData = Partial<{
  allowedUsers: User[];
  disallowedUsers: User[];
  assignments: number[];
  lockedAssignments: number[];
  lockedAssignmentsTooltip: string;
  heading: string;
  subheading: string;
  preventAdminRemove: boolean;
  showCountBeforeSubheading: boolean;
  minSelectedAdmins: number;
  maxSelectedAdmins: number;
  additionalUserSelectionOptions: AdditionalUserSelectionOption[];
  additionalUserAssignments: string[];
  inviteModeEnabled?: boolean;
  shareOnTheGoEnabled?: boolean;
  disabledUserCopy?: string;
  actionLabel?: string;
  hasActionLabelCount?: boolean;
  withInviteTeamMembersButton?: boolean;
  hasStepBack?: boolean;
  stepText?: string;
}>;

export type AdminsSelectDialogResult = {
  users: User[];
  additionalUserSelectionOptions: AdditionalUserSelectionOption[];
  goBack?: boolean;
};

@Component({
  selector: 'rt-admins-select-dialog',
  templateUrl: './admins-select-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AdminsSelectDialogFormComponent],
  providers: [SelectAdminsAnalytics],
})
export class AdminsSelectDialogComponent implements DialogComponentI<
  AdminsSelectDialogData,
  AdminsSelectDialogResult | null
> {
  private readonly store = inject(Store);
  private readonly dialog = inject(DialogRef);
  private readonly i18n = inject(I18nService);

  public currentAdmin: CurrentUser;
  public assignments: number[] = [];
  public lockedAssignments: number[] = [];
  public lockedAssignmentsTooltip: string | null = null;
  public heading: string;
  public subheading: string;
  public preventAdminRemove: boolean = false;
  public allowedUsers: User[] = [];
  public disallowedUsers: User[] = [];
  public showCountBeforeSubheading: boolean = false;
  public minSelectedAdmins: number = 0;
  public maxSelectedAdmins: number = 0;
  public offerId?: number;
  public inviteModeEnabled = false;
  public withInviteTeamMembersButton = false;
  public disabledUserCopy: string;
  public disabledUsersHeader: string = this.i18n.translate(
    'app.candidate.admin_select.no_access.header',
  );
  public additionalUserSelectionOptions: AdditionalUserSelectionOption[] = [];
  public additionalUserAssignments: string[] = [];
  public actionLabel?: string;
  public hasActionLabelCount: boolean;
  public hasStepBack = false;
  public stepText?: string;
  protected selectAdminsAnalytics = inject(SelectAdminsAnalytics);
  private ngOnDestroy$ = new Subject<void>();
  private router = inject(Router);
  private location = inject(Location);

  constructor() {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, take(1))
      .subscribe(currentAdmin => {
        this.currentAdmin = currentAdmin;
      });
  }

  public setDialogData(data: AdminsSelectDialogData) {
    this.assignments = data.assignments || [];
    this.lockedAssignments = data.lockedAssignments || [];
    this.lockedAssignmentsTooltip = data.lockedAssignmentsTooltip || null;
    this.heading = data.heading || this.i18n.translate('app.tasks.assignments.heading');
    this.subheading = data.subheading || this.i18n.translate('app.tasks.assignments.subheading');
    this.preventAdminRemove = data.preventAdminRemove || false;
    this.showCountBeforeSubheading = data.showCountBeforeSubheading || false;
    this.minSelectedAdmins = data.minSelectedAdmins || 0;
    this.maxSelectedAdmins = data.maxSelectedAdmins || 0;
    this.inviteModeEnabled = data.inviteModeEnabled ?? false;
    this.withInviteTeamMembersButton = data.withInviteTeamMembersButton ?? false;
    this.actionLabel = data.actionLabel;
    this.hasActionLabelCount = data.hasActionLabelCount ?? false;
    this.disabledUserCopy =
      data.disabledUserCopy ||
      this.i18n.translate('app.candidate.admin_select.no_access.tooltip_share_on_the_go');
    this.additionalUserSelectionOptions = data.additionalUserSelectionOptions || [];
    this.additionalUserAssignments = data.additionalUserAssignments || [];
    this.hasStepBack = data.hasStepBack ?? false;
    this.stepText = data?.stepText;

    if (data.disallowedUsers) {
      this.disallowedUsers = data.disallowedUsers;
      if (
        this.disallowedUsers.every(user => !user.roleAbilities.includes(Ability.ViewCandidates))
      ) {
        this.disabledUsersHeader = this.i18n.translate(
          'app.candidate.admin_select.no_access.no_view_candidates.header',
        );
        this.disabledUserCopy =
          data.disabledUserCopy ||
          this.i18n.translate('app.candidate.admin_select.no_access.no_view_candidates.tooltip');
      }
    }

    if (data.allowedUsers) {
      this.allowedUsers = data.allowedUsers;
    } else {
      this.store
        .select(UserSelectors.entities())
        .pipe(take(1))
        .subscribe(allowedUsers => (this.allowedUsers = allowedUsers || []));
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancel(): void {
    this.dialog.close(null);
  }

  public submit(result: AdminsSelectDialogResult): void {
    this.dialog.close(result);
  }

  public goBack(result: AdminsSelectDialogResult): void {
    this.dialog.close(<AdminsSelectDialogResult>{ ...result, goBack: true });
  }

  public get currentAdminId(): number {
    return this.currentAdmin.id;
  }

  protected redirectToInvitingMembers() {
    const urlTree = this.router.createUrlTree(['/settings/members'], {
      queryParams: { invite: true },
      queryParamsHandling: 'merge',
    });
    window.open(this.location.prepareExternalUrl(this.router.serializeUrl(urlTree)), '_blank');
  }
}
